<script>
import { mapGetters } from 'vuex';
import ConversationHeader from './ConversationHeader.vue';
import DashboardAppFrame from '../DashboardApp/Frame.vue';
import EmptyState from './EmptyState/EmptyState.vue';
import MessagesView from './MessagesView.vue';
import ContactNotes from 'dashboard/routes/dashboard/conversation/contact/ContactNotes.vue';
import ConversationWorkflowTimeline from './ConversationWorkflowTimeline.vue';

export default {
  components: {
    ConversationHeader,
    DashboardAppFrame,
    EmptyState,
    MessagesView,
    ContactNotes,
    ConversationWorkflowTimeline,
  },
  props: {
    inboxId: {
      type: [Number, String],
      default: '',
      required: false,
    },
    isInboxView: {
      type: Boolean,
      default: false,
    },
    isContactPanelOpen: {
      type: Boolean,
      default: true,
    },
    isOnExpandedLayout: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return { activeIndex: 0 };
  },
  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      dashboardApps: 'dashboardApps/getRecords',
    }),
    dashboardAppTabs() {
      return [
        {
          key: 'messages',
          index: 0,
          name: this.$t('CONVERSATION.DASHBOARD_APP_TAB_MESSAGES'),
        },
        {
          key: 'documents',
          index: 1,
          name: this.$t('CONVERSATION.DASHBOARD_APP_TAB_DOCUMENTS'),
        },
        {
          key: 'internal-messages',
          index: 2,
          name: this.$t('CONVERSATION.DASHBOARD_APP_TAB_INTERNAL_MESSAGES'),
        },
        {
          key: 'notes',
          index: 3,
          name: this.$t('CONVERSATION.DASHBOARD_APP_TAB_NOTES'),
        },
        {
          key: 'workflow-timeline',
          index: 4,
          name: 'سير المحادثة',
        },
        ...this.dashboardApps.map((dashboardApp, index) => ({
          key: `dashboard-${dashboardApp.id}`,
          index: index + 5,
          name: dashboardApp.title,
        })),
      ];
    },
    activeConversationContent() {
      if (this.activeIndex === 1) return 'documents';
      if (this.activeIndex === 2) return 'internal';
      return 'messages';
    },
    conversationContactId() {
      return this.currentChat.meta?.sender?.id || null;
    },
    showContactPanel() {
      return this.isContactPanelOpen && this.currentChat.id;
    },
  },
  watch: {
    'currentChat.inbox_id': {
      immediate: true,
      handler(inboxId) {
        if (inboxId) {
          this.$store.dispatch('inboxAssignableAgents/fetch', [inboxId]);
        }
      },
    },
    'currentChat.id'() {
      this.fetchLabels();
      this.activeIndex = 0;
    },
  },
  mounted() {
    this.fetchLabels();
    this.$store.dispatch('dashboardApps/get');
  },
  methods: {
    fetchLabels() {
      if (!this.currentChat.id) {
        return;
      }
      this.$store.dispatch('conversationLabels/get', this.currentChat.id);
    },
    onDashboardAppTabChange(index) {
      this.activeIndex = index;
    },
  },
};
</script>

<template>
  <div
    class="conversation-details-wrap flex flex-col min-w-0 w-full bg-n-surface-1 relative"
    :class="{
      'border-l rtl:border-l-0 rtl:border-r border-n-weak': !isOnExpandedLayout,
    }"
  >
    <ConversationHeader
      v-if="currentChat.id"
      :chat="currentChat"
      :show-back-button="isOnExpandedLayout && !isInboxView"
      :class="{
        'border-b border-b-n-weak !pt-2': !dashboardApps.length,
      }"
    />
    <woot-tabs
      v-if="currentChat.id"
      :index="activeIndex"
      class="h-10"
      @change="onDashboardAppTabChange"
    >
      <woot-tabs-item
        v-for="tab in dashboardAppTabs"
        :key="tab.key"
        :index="tab.index"
        :name="tab.name"
        :show-badge="false"
        is-compact
      />
    </woot-tabs>
    <div v-show="activeIndex < 5" class="flex h-full min-h-0 m-0">
      <MessagesView
        v-if="currentChat.id"
        v-show="activeIndex < 3"
        :inbox-id="inboxId"
        :is-inbox-view="isInboxView"
        :active-content="activeConversationContent"
      />
      <div
        v-if="currentChat.id && activeIndex === 3"
        class="conversation-panel flex flex-shrink flex-grow basis-px flex-col overflow-y-auto relative h-full m-0"
      >
        <ContactNotes
          :contact-id="conversationContactId"
          read-only
          allow-create
        />
      </div>
      <ConversationWorkflowTimeline
        v-if="currentChat.id && activeIndex === 4"
        :conversation="currentChat"
      />
      <EmptyState
        v-if="!currentChat.id && !isInboxView"
        :is-on-expanded-layout="isOnExpandedLayout"
      />
      <slot />
    </div>

    <DashboardAppFrame
      v-for="(dashboardApp, index) in dashboardApps"
      v-show="activeIndex - 5 === index"
      :key="currentChat.id + '-' + dashboardApp.id"
      :is-visible="activeIndex - 5 === index"
      :config="dashboardApps[index].content"
      :position="index"
      :current-chat="currentChat"
    />
  </div>
</template>
